import { memo, useCallback, useState } from "react";
import * as Bananas from "bananas-commerce-admin";

import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  SelectChangeEvent,
  Stack,
  Switch,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";

import dayjs from "dayjs";
import { useSnackbar } from "notistack";

import Select, { ReactNodeOption } from "@/components/Select";
import SubtitledLabel from "@/components/SubtitledLabel";

import { Case } from "@/extensions/klarahill/types";
import AdMeta from "@/extensions/obituary/components/AdMeta";
import AdStatusBadge from "@/extensions/obituary/components/AdStatusBadge";
import PublishDateDialog from "@/extensions/obituary/components/PublishDateDialog";
import { Ad, layoutName, PatchAdPayload } from "@/extensions/obituary/types";

import EditNonPublishableAdDialog from "../../components/EditNonPublishableAdDialog";
import { useObituaryDialog } from "../../contexts/ObituaryDialogContext";
import { statusLabels } from "../../utils/statusLabels";

import { Nullish, Timedate } from "@/utils/types";

const REMOVE_AD_CONFIRMATION =
  "Vill du verkligen ta bort annonsen? Detta går inte att ångra.";

const styles: Record<string, SxProps<Theme>> = {
  container: {
    position: "relative",
  },
  root: {
    position: "sticky",
    zIndex: 1,
    display: "flex",
    flexDirection: { md: "row", xs: "column" },
    gap: 1,
    width: "100%",
    minWidth: { lg: 900 },
    maxWidth: { md: 900, xs: "100%" },
    overflow: "hidden",
  },

  rootHasVersions: {
    boxShadow: "2px 2px 2px 0px rgba(0, 0, 0, 0.1)",
  },

  cardStack: {
    position: "absolute",
    height: "100%",
    width: "100%",
    top: 8,
    left: 8,
    transform: "rotate(0.5deg)",
  },

  preview: {
    objectFit: "contain",
    width: "100%",
    height: "100%",
    maxWidth: { md: "none", xs: 150 },
  },

  select: {
    minWidth: 0,
    fontSize: 12,
    fontWeight: "500",
    lineHeight: 1.5,
    color: "text.secondary",
    textTransform: "uppercase",

    "& div[role='combobox']": {
      py: 0.75,
    },
  },

  option: {
    fontSize: 12,
    fontWeight: "500",
    lineHeight: 1.5,
    textTransform: "uppercase",

    "& span": {
      fontSize: 12,
      fontWeight: "500",
      lineHeight: 1.5,
      color: "text.secondary",
      marginLeft: 1,
    },
  },

  header: {
    flexDirection: { md: "row", xs: "column" },
    justifyContent: "space-between",
    width: "100%",
    gap: 1,
  },

  body: {
    height: "100%",
  },

  buttons: {
    flexDirection: { md: "row-reverse", xs: "column" },
    mt: "auto",
    pt: 4,
  },

  approve: {
    display: "flex",
    flexDirection: "row-reverse",
    width: "100%",
    justifyContent: "space-between",
    ml: 0,
    mr: 0,
  },
};

const figureStyle: SxProps<Theme> = (theme) => ({
  bgcolor: "background.paper",
  ...theme.applyStyles("light", {
    backgroundColor: "#e5e5e5",
  }),
  p: 2,
  m: 0,
  borderColor: "divider",
  borderWidth: 0,
  borderRightWidth: { md: 1, xs: 0 },
  borderBottomWidth: { md: 0, xs: 1 },
  borderStyle: "solid",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: { md: 200, xs: 100 },
});

type AdVersions = Record<number, Ad>;

export interface AdCardProps extends Bananas.CardProps {
  c: Case | null;
  ad: Ad;
  onApprove?: (ad: Ad) => void;
  onChangeDate?: (ad: Ad) => void;
  onDeleted: (ad: Ad) => void;
  onEdit: (ad: Ad) => void;
}

const AdCard: React.FC<AdCardProps> = ({
  c,
  ad: latestAd,
  onApprove,
  onChangeDate,
  onDeleted,
  onEdit,
  ...props
}) => {
  const api = Bananas.useApi();
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog } = useObituaryDialog();

  const [ad, setAd] = useState<Ad>(latestAd);
  const [adVersions, setAdVersions] = useState<AdVersions>({});

  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [editNonPublishableDialogOpen, setEditNonPublishableDialogOpen] =
    useState(false);

  const hasVersions = latestAd.version !== 1;
  const isLatestVersion = ad.version === latestAd.version;
  const isApproved = ad.approved_at != null;
  const isSent = ad.status === "sent";

  /// Callbacks

  const handleVersionSelectOpen = useCallback(async () => {
    if (adVersions[ad.version] != null) return;

    const action = api.operations["obituary.ad:retrieve-versions"];
    if (action == null)
      throw new Error('No action "obituary.ad:retrieve-versions"');

    const response = await action.call({ params: { obituary_ad_id: ad.id } });

    if (response.ok) {
      const versions = (await response.json()) as Ad[];
      setAdVersions(
        versions.reduce((acc, version) => {
          acc[version.version] = version;
          return acc;
        }, {} as AdVersions),
      );
    }
  }, [ad, adVersions]);

  const updateAd = useCallback(
    async (payload: PatchAdPayload) => {
      const action = api.operations["obituary.ad:update"];
      if (action == null) throw new Error('No action "obituary.ad:update"');

      const response = await action.call({
        params: { obituary_ad_id: ad.id },
        body: payload,
      });

      if (response.ok) {
        const updatedAd = (await response.json()) as Ad;
        setAd(updatedAd);
        return updatedAd;
      } else {
        enqueueSnackbar(
          "Fel vid uppdatering av korrekturstatus. Försök igen eller kontakta support om felet kvarstår.",
          { variant: "error" },
        );
      }
    },
    [ad, api],
  );

  const handleApprove = useCallback(async () => {
    if (isApproved && !confirm("Vill du neka godkännandet?")) return;
    const updatedAd = await updateAd({ approve: !isApproved });
    if (updatedAd != null) onApprove?.(updatedAd);
  }, [ad, api]);

  const handleChangeDate = useCallback(
    async (
      publication_date: Nullish<Timedate>,
      editing_deadline: Nullish<Timedate>,
    ) => {
      if (publication_date == null || editing_deadline == null) return;

      const updatedAd = await updateAd({
        deadline: { publication_date, editing_deadline },
      });
      if (updatedAd != null) onChangeDate?.(updatedAd);
    },
    [ad],
  );

  const handleDelete = useCallback(async () => {
    if (!confirm(REMOVE_AD_CONFIRMATION)) return;

    const action = api.operations["obituary.ad:delete"];
    if (!action) throw new Error('Invalid action "obituary.ad:delete"');

    const response = await action.call({ params: { obituary_ad_id: ad.id } });
    if (response.ok) {
      onDeleted?.(ad);
    } else {
      enqueueSnackbar(
        "Fel vid borttagning av annons. Försök igen eller kontakta support om felet kvarstår.",
        { variant: "error" },
      );
    }
  }, [api, ad.id]);

  const handleEdit = useCallback(async () => {
    if (ad.status === "sent" || ad.status === "cancelled") {
      setEditNonPublishableDialogOpen(true);
      return;
    }

    onEdit?.(ad);
  }, [ad]);

  const handleCreateCopy = useCallback(async () => {
    if (!c) throw new Error("Case is required to create an ad");

    const action = api.operations["obituary-ad.version:create"];
    if (!action) throw new Error('Invalid action "obituary-ad.version:create"');

    const response = await action.call({ params: { obituary_ad_id: ad.id } });
    if (response.ok) {
      const data = (await response.json()) as Ad;
      onEdit?.(data);
    } else {
      enqueueSnackbar(
        "Fel vid skapande av annons. Försök igen eller kontakta support om felet kvarstår.",
        { variant: "error" },
      );
    }
  }, [api, c, ad]);

  const handleVersionSelectChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const version = adVersions[event.target.value as number];
      if (version != null) {
        setAd(version);
      } else {
        enqueueSnackbar("Fel vid hämtning av annonsversion", {
          variant: "error",
        });
      }
    },
    [adVersions],
  );

  const handleCancelBooking = useCallback(async () => {
    if (!confirm("Vill du verkligen avboka annonsen?")) return;

    const action = api.operations["obituary.ad:cancel"];
    if (!action) throw new Error('Invalid action "obituary.ad:cancel"');

    const response = await action.call({ params: { obituary_ad_id: ad.id } });

    if (response.ok) {
      const updatedAd = (await response.json()) as Ad;
      setAd(updatedAd);
    } else {
      enqueueSnackbar(
        "Fel vid avbokning av annons. Försök igen eller kontakta support om felet kvarstår.",
        { variant: "error" },
      );
    }
  }, [api, ad.id]);

  const handleCloseDateDialog = useCallback(() => setDateDialogOpen(false), []);

  return (
    <Box sx={styles.container}>
      {hasVersions && (
        <Bananas.Card
          component="div"
          sx={{ ...styles.root, ...styles.cardStack } as SxProps}
        />
      )}
      <Bananas.Card
        component="article"
        sx={
          {
            ...styles.root,
            ...(hasVersions && styles.rootHasVersions),
          } as SxProps
        }
        {...props}
      >
        {ad.preview_url && ad.preview_url !== "" && (
          <Box aria-label="Förhandsvisning" component="figure" sx={figureStyle}>
            <Box component="img" src={ad.preview_url} sx={styles.preview} />
          </Box>
        )}

        <Stack gap={2} p={2} sx={{ width: "100%" }}>
          <Stack component="header" sx={styles.header}>
            <Stack>
              <Typography variant="h5">{ad.publication.name ?? "—"}</Typography>

              <Typography variant="subtitle1">
                {layoutName[ad.layout_variant]}
              </Typography>
            </Stack>

            <Stack alignItems="center" direction="row" spacing={1}>
              <AdStatusBadge type={ad.status} />
              {hasVersions && (
                <Select
                  menuItemProps={{ sx: styles.option }}
                  MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "right" },
                    transformOrigin: { vertical: "top", horizontal: "right" },
                  }}
                  options={
                    (Object.values(adVersions) ?? ad).map((version) => ({
                      label: (
                        <Typography variant="inherit">
                          Version {version.version}{" "}
                          <span>({statusLabels[version.status]})</span>
                        </Typography>
                      ),
                      value: version.version.toString(),
                    })) as ReactNodeOption[]
                  }
                  renderValue={(value) => {
                    const version = adVersions[value as number];
                    return `Version ${version?.version ?? ad.version}`;
                  }}
                  sx={styles.select}
                  value={ad.version}
                  onChange={handleVersionSelectChange}
                  onOpen={handleVersionSelectOpen}
                />
              )}
            </Stack>
          </Stack>

          <Stack divider={<Divider flexItem />} gap={2} sx={styles.body}>
            <AdMeta showPublishInfo ad={ad} />

            <FormControlLabel
              control={
                <Switch checked={isApproved} disabled={!isLatestVersion} />
              }
              label={
                <SubtitledLabel
                  subtitle={
                    isApproved
                      ? `Korrekturen godkändes ${dayjs(ad.approved_at).format("YYYY-MM-DD")}`
                      : null
                  }
                  sx={{ gap: 0.5 }}
                  title="Godkänd korrektur"
                />
              }
              sx={styles.approve}
              onChange={handleApprove}
            />
          </Stack>

          <Stack gap={2} sx={styles.buttons}>
            <Button
              disabled={!isLatestVersion}
              variant="contained"
              onClick={handleEdit}
            >
              Redigera
            </Button>
            {isSent ? (
              <Button
                disabled={ad.status === "archived"}
                onClick={handleCancelBooking}
              >
                Avboka annons
              </Button>
            ) : (
              isLatestVersion &&
              ad.status !== "pending_mediahouse_review" && (
                <Button onClick={openDialog("publish")}>Boka annons</Button>
              )
            )}
            {isLatestVersion && !isSent && (
              <Button onClick={handleDelete}>Ta bort</Button>
            )}
          </Stack>
        </Stack>
      </Bananas.Card>

      {dateDialogOpen && (
        <PublishDateDialog
          key={ad.id}
          ad={ad}
          open={dateDialogOpen}
          onClose={handleCloseDateDialog}
          onSubmit={handleChangeDate}
        />
      )}

      {editNonPublishableDialogOpen && (
        <EditNonPublishableAdDialog
          adStatus={ad.status}
          open={editNonPublishableDialogOpen}
          onClose={() => setEditNonPublishableDialogOpen(false)}
          onSubmit={handleCreateCopy}
        />
      )}
    </Box>
  );
};

export default memo(AdCard);
